import React, { useState, useEffect } from "react";
import styled, { css } from "styled-components";
import {
  Button,
  Select,
  Tooltip,
  Spin,
  Radio,
  Switch,
  Input,
  Form,
  DatePicker,
} from "antd";
import {
  SearchOutlined,
  CheckCircleOutlined,
  InfoCircleOutlined,
  LeftOutlined,
  RightOutlined,
  UserOutlined,
  MailOutlined,
  PhoneOutlined,
  CalendarOutlined,
} from "@ant-design/icons";
import { CiWarning } from "react-icons/ci";

import dayjs from "dayjs";
import toast from "react-hot-toast";
import { useNavigate } from "react-router-dom";
import {
  fetchGuestHouseOutlets,
  checkRoomAvailability,
  //fetchRoomTypes,
  getProductOrServiceDetails,
} from "../../Store/Apis/ReservationApi";
import "./BookNowModal.css"; // Import external styles
import CustomerSelection from "./CustomerSelection";
import TermsAndConditions from "../../Components/TermsAndConditions";

const { Option } = Select;
const { RangePicker } = DatePicker;

const BookNowModal = ({ onClose, index }) => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("creditApplication");
  const [rooms, setRooms] = useState(1);
  const [adults, setAdults] = useState(1);
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [outlets, setOutlets] = useState([]);
  const [selectedOutletId, setSelectedOutletId] = useState(3);
  const [selectedOutletName, setSelectedOutletName] = useState("");
  const [loading, setLoading] = useState(false);
  const [availability, setAvailability] = useState(null);
  const [options, setOptions] = useState([]);
  const [rates, setRates] = useState({});
  const [accommodationDetails, setAccommodationDetails] = useState(null);

  // Booking type and guest details
  const [bookingType, setBookingType] = useState("individual"); // individual or company
  const [isQuotation, setIsQuotation] = useState(false);
  const [guestDetails, setGuestDetails] = useState([]);
  const [form] = Form.useForm();

  const [customerInfo, setCustomerInfo] = useState(null);
  const [showCustomerSelection, setShowCustomerSelection] = useState(false);
  const [accountOnHold, setAccountOnHold] = useState(false);

  // Calendar state
  const [currentMonthDate, setCurrentMonthDate] = useState(dayjs());
  const [hoverDate, setHoverDate] = useState(null);

  // Selected room states
  const [selectedRooms, setSelectedRooms] = useState({
    standard: 0,
    selfCatering: 0,
    disabilityFriendly: 0,
  });

  const [roomTypeSelections, setRoomTypeSelections] = useState({
    standard: [],
    selfCatering: [],
    disabilityFriendly: [],
  });

  useEffect(() => {
    console.log("Rooms is -----", rooms);
  }, [rooms]);

  // Initialize guest details when rooms change
  useEffect(() => {
    const totalRooms = Object.values(selectedRooms).reduce((a, b) => a + b, 0);
    const newGuestDetails = [];

    for (let i = 0; i < totalRooms; i++) {
      newGuestDetails.push({
        roomNumber: i + 1,
        fullName: "",
        email: "",
        checkInDate: null,
        checkOutDate: null,
      });
    }

    setGuestDetails(newGuestDetails);
  }, [selectedRooms]);

  // Navigate between tabs based on booking type
  useEffect(() => {
    if (bookingType === "individual" && startDate && endDate) {
      setTimeout(() => {
        setActiveTab("roomsGuests");
      }, 300);
    }
  }, [startDate, endDate, bookingType]);

  // Check if all room types are selected
  const areAllRoomTypesSelected = () => {
    const totalSelectedRooms = Object.values(selectedRooms).reduce(
      (a, b) => a + b,
      0
    );

    if (totalSelectedRooms !== rooms) return false;

    let totalDropdowns = 0;
    let filledDropdowns = 0;

    Object.entries(selectedRooms).forEach(([roomType, count]) => {
      if (count > 0) {
        totalDropdowns += count;
        const selections = roomTypeSelections[roomType] || [];
        selections.forEach((selection) => {
          if (selection && selection !== "") {
            filledDropdowns++;
          }
        });
      }
    });

    return filledDropdowns === totalDropdowns;
  };

  // Move to guest details tab when all room types are selected
  useEffect(() => {
    if (areAllRoomTypesSelected() && activeTab === "roomsGuests") {
      setTimeout(() => {
        setActiveTab("guestDetails");
      }, 300);
    }
  }, [roomTypeSelections, selectedRooms, rooms, activeTab]);

  // Initialize room type selections when room count changes
  useEffect(() => {
    setSelectedRooms({
      standard: 0,
      selfCatering: 0,
      disabilityFriendly: 0,
    });

    setRoomTypeSelections({
      standard: [],
      selfCatering: [],
      disabilityFriendly: [],
    });
  }, [rooms]);

  // Calculate total selected rooms
  const totalSelectedRooms = Object.values(selectedRooms).reduce(
    (a, b) => a + b,
    0
  );

  // Calendar navigation functions
  const goToPreviousMonth = () => {
    setCurrentMonthDate(currentMonthDate.subtract(1, "month"));
  };

  const goToNextMonth = () => {
    setCurrentMonthDate(currentMonthDate.add(1, "month"));
  };

  // Date selection functions
  const handleDateClick = (date) => {
    const clickedDate = dayjs(date);

    if (!startDate || (startDate && endDate)) {
      setStartDate(clickedDate);
      setEndDate(null);
    } else {
      if (clickedDate.isBefore(startDate)) {
        setEndDate(startDate);
        setStartDate(clickedDate);
      } else {
        setEndDate(clickedDate);
      }
    }
  };

  // Check if date is disabled (in the past)
  const isDateDisabled = (date) => {
    return dayjs(date).isBefore(dayjs().startOf("day"));
  };

  // Check if date is within selected range
  const isInSelectedRange = (date) => {
    if (!startDate || !date) return false;
    if (!endDate) return dayjs(date).isSame(startDate, "day");

    return (
      (dayjs(date).isAfter(startDate.startOf("day"), "day") &&
        dayjs(date).isBefore(endDate.endOf("day"), "day")) ||
      dayjs(date).isSame(startDate, "day") ||
      dayjs(date).isSame(endDate, "day")
    );
  };

  // Check if date is hovering for potential end date
  const isInHoverRange = (date) => {
    if (!startDate || !hoverDate || endDate) return false;

    const isAfterStart =
      dayjs(date).isAfter(startDate, "day") ||
      dayjs(date).isSame(startDate, "day");
    const isBeforeHover =
      dayjs(date).isBefore(hoverDate, "day") ||
      dayjs(date).isSame(hoverDate, "day");

    return isAfterStart && isBeforeHover;
  };

  // Handle mouse hover on dates for visual feedback
  const handleDateHover = (date) => {
    if (startDate && !endDate) {
      setHoverDate(dayjs(date));
    }
  };

  // Generate calendar days for a month
  const generateCalendarDays = (monthDate) => {
    const daysInMonth = monthDate.daysInMonth();
    const firstDayOfMonth = monthDate.startOf("month").day();
    const days = [];

    for (let i = 0; i < firstDayOfMonth; i++) {
      days.push(null);
    }

    for (let i = 1; i <= daysInMonth; i++) {
      const dayDate = monthDate.date(i);
      days.push(dayDate);
    }

    return days;
  };

  const getTabsForBookingType = () => {
    const baseTabs = [
      {
        key: "creditApplication",
        label: "Credit Application",
        info: "Book Now, Pay Later",
      },
      {
        key: "bookingType",
        label: "Booking Type",
        info:
          bookingType === "individual"
            ? "Individual Booking"
            : "Company Booking",
      },
    ];

    // Add customer selection tab after booking type
    baseTabs.push({
      key: "customerSelection",
      label: "Customer Info",
      info: customerInfo?.isExisting
        ? customerInfo.customerType === "individual"
          ? "Existing Customer"
          : "Existing Company"
        : "New Customer",
      disabled: !bookingType,
    });

    if (bookingType === "individual") {
      baseTabs.push({
        key: "travelDates",
        label: "Check In/Out",
        info:
          startDate && endDate
            ? `${startDate.format("DD MMM YYYY")} - ${endDate.format(
                "DD MMM YYYY"
              )}`
            : "",
        disabled: !customerInfo,
      });
    }

    baseTabs.push({
      key: "roomsGuests",
      label: "Rooms & Guests",
      info: `${rooms} Room${rooms > 1 ? "s" : ""}, ${adults} Guest${
        adults > 1 ? "s" : ""
      }`,
      disabled:
        bookingType === "individual"
          ? !startDate || !endDate || !customerInfo
          : !customerInfo,
    });

    baseTabs.push({
      key: "guestDetails",
      label: bookingType === "individual" ? "Booking Options" : "Guest Details",
      info: isQuotation ? " (Quotation)" : "",
      disabled: !areAllRoomTypesSelected(),
    });

    return baseTabs;
  };

  const handleCustomerSelected = (customerData) => {
    setCustomerInfo(customerData);
    console.log("Customer selected:", customerData);

    // Check if account is on hold
    const isAccountOnHold =
      customerData?.customerData?.creditAccStatus === "on hold";

    if (isAccountOnHold) {
      setAccountOnHold(true);
      return; // Stop further processing
    }

    // Move to next appropriate tab
    if (bookingType === "individual") {
      setActiveTab("travelDates");
    } else {
      setActiveTab("roomsGuests");
    }
  };

  // Update selected room counts
  const handleRoomSelectionChange = (roomType, isChecked) => {
    const newCount = isChecked
      ? selectedRooms[roomType] + 1
      : selectedRooms[roomType] - 1;

    if (isChecked && totalSelectedRooms >= rooms) {
      toast.error(`You can only select a total of ${rooms} room(s).`);
      return;
    }

    setSelectedRooms((prev) => ({
      ...prev,
      [roomType]: newCount,
    }));

    if (isChecked) {
      setRoomTypeSelections((prev) => ({
        ...prev,
        [roomType]: [...(prev[roomType] || []), ""],
      }));
    } else {
      setRoomTypeSelections((prev) => ({
        ...prev,
        [roomType]: (prev[roomType] || []).slice(0, -1),
      }));
    }
  };

  // Handle room type change for a specific room
  const handleRoomTypeChange = (value, roomType, index) => {
    setRoomTypeSelections((prev) => {
      const currentSelections = prev[roomType] || [];
      const updated = [...currentSelections];

      while (updated.length <= index) {
        updated.push("");
      }

      updated[index] = value;
      return {
        ...prev,
        [roomType]: updated,
      };
    });
  };

  // Update rooms with validation
  const updateRooms = (newCount) => {
    const roomCount = Math.max(1, newCount);
    if (adults < roomCount) {
      toast.error("Each room must have at least one person.");
      return;
    }
    setRooms(roomCount);
    setSelectedRooms({
      standard: 0,
      selfCatering: 0,
      disabilityFriendly: 0,
    });
  };

  // Update adults with validation
  const updateAdults = (newCount) => {
    const adultCount = Math.max(1, newCount);
    if (adultCount > rooms * 2) {
      toast.error("Each room can only accommodate a maximum of 2 people.");
      return;
    }
    setAdults(adultCount);
  };

  // Handle guest detail changes
  const handleGuestDetailChange = (roomIndex, field, value) => {
    const updatedGuestDetails = [...guestDetails];
    updatedGuestDetails[roomIndex] = {
      ...updatedGuestDetails[roomIndex],
      [field]: value,
    };
    setGuestDetails(updatedGuestDetails);
  };

  // Handle individual guest date changes
  const handleGuestDateChange = (roomIndex, dates) => {
    if (dates && dates.length === 2) {
      const updatedGuestDetails = [...guestDetails];
      updatedGuestDetails[roomIndex] = {
        ...updatedGuestDetails[roomIndex],
        checkInDate: dates[0],
        checkOutDate: dates[1],
      };
      setGuestDetails(updatedGuestDetails);
    }
  };

  useEffect(() => {
    const getOutlets = async () => {
      try {
        const fetchedOutlets = await fetchGuestHouseOutlets();

        if (fetchedOutlets && fetchedOutlets.length > 0) {
          setOutlets(fetchedOutlets);

          const outlet = fetchedOutlets.find((outlet) => outlet.outletId === 3);
          if (outlet) {
            setSelectedOutletId(3);
            setSelectedOutletName(outlet.outletName);
          } else {
            console.error("Outlet with ID 3 not found in fetched outlets.");
          }
        } else {
          console.error("No outlets fetched from the API.");
        }
      } catch (error) {
        console.error("Error fetching outlets:", error);
        toast.error("Failed to fetch outlets. Please try again later.");
      }
    };

    getOutlets();
  }, []);

  // Validate guest details
  const validateGuestDetails = () => {
    if (bookingType === "individual") {
      return true;
    }

    for (let i = 0; i < guestDetails.length; i++) {
      const guest = guestDetails[i];
      if (
        !guest.fullName ||
        !guest.email ||
        !guest.checkInDate ||
        !guest.checkOutDate
      ) {
        toast.error(`Please fill in all details for Room ${i + 1}`);
        return false;
      }

      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(guest.email)) {
        toast.error(`Please enter a valid email for Room ${i + 1}`);
        return false;
      }
    }
    return true;
  };

  // Check if guest details are complete
  const areGuestDetailsComplete = () => {
    if (bookingType === "individual") {
      return true;
    }

    return guestDetails.every(
      (guest) =>
        guest.fullName && guest.email && guest.checkInDate && guest.checkOutDate
    );
  };

  const extractedCustomerData =
    customerInfo?.isExisting && customerInfo?.customerData
      ? {
          // For individuals
          customerID: customerInfo.customerData.customerID,
          name: customerInfo.customerData.customerName,
          email: customerInfo.customerData.email,
          phone: customerInfo.customerData.phoneNumber,
          // For companies
          companyID: customerInfo.customerData.companyID,
          companyName: customerInfo.customerData.companyName,
          companyEmail: customerInfo.customerData.companyEmailAddress,
        }
      : null;

  // Handle checking availability
  const handleCheckAvailability = async () => {
    if (bookingType === "individual") {
      if (!startDate || !endDate || !selectedOutletId) {
        toast.error("Please select a date range and an outlet.");
        return;
      }
    }

    if (!validateGuestDetails()) {
      return;
    }

    setLoading(true);

    try {
      let reservationData;

      if (bookingType === "individual") {
        const data = await checkRoomAvailability(
          startDate.format("YYYY-MM-DD"),
          endDate.format("YYYY-MM-DD"),
          selectedOutletId
        );

        if (data) {
          const availableRoomsCount = data.availableRoomsCount;

          if (availableRoomsCount < rooms) {
            toast.error(
              `Only ${availableRoomsCount} room(s) are available, but you requested ${rooms} room(s).`
            );
            return;
          }

          const additionalGuests = Math.max(0, adults - rooms);
          const nights = endDate.diff(startDate, "day");

          reservationData = {
            outletId: selectedOutletId,
            outletName: selectedOutletName,
            guests: adults,
            nights: nights,
            rooms: rooms,
            startDate: startDate.format("YYYY-MM-DD"),
            endDate: endDate.format("YYYY-MM-DD"),
            additionalGuests: additionalGuests,
            bookingType: bookingType,
            isQuotation: isQuotation,
            statusID: isQuotation ? 11 : 7,
            guestDetails: [],
            // Include customer information
            customerInfo: {
              ...customerInfo,
              extractedData: extractedCustomerData,
            },
          };
        } else {
          toast.error("Failed to check availability. Please try again.");
          return;
        }
      } else {
        // Company booking validation...
        for (let guest of guestDetails) {
          const data = await checkRoomAvailability(
            guest.checkInDate.format("YYYY-MM-DD"),
            guest.checkOutDate.format("YYYY-MM-DD"),
            selectedOutletId
          );

          if (data && data.availableRoomsCount < 1) {
            toast.error(`Room ${guest.roomNumber} dates are not available.`);
            return;
          }
        }

        reservationData = {
          outletId: selectedOutletId,
          outletName: selectedOutletName,
          guests: guestDetails.length,
          rooms: guestDetails.length,
          bookingType: bookingType,
          isQuotation: isQuotation,
          statusID: isQuotation ? 11 : 7,
          guestDetails: guestDetails.map((guest, index) => ({
            roomNumber: index + 1,
            fullName: guest.fullName,
            email: guest.email,
            checkInDate: guest.checkInDate.format("YYYY-MM-DD"),
            checkOutDate: guest.checkOutDate.format("YYYY-MM-DD"),
            nights: guest.checkOutDate.diff(guest.checkInDate, "day"),
            isCompanyBooking: true,
          })),
          // Include customer information
          customerInfo: customerInfo,
        };
      }

      if (!reservationData) {
        toast.error("Failed to create reservation data. Please try again.");
        return;
      }

      console.log("Navigating with reservation data:", reservationData);
      console.log("Navigating with rates:", rates);
      console.log("Customer Info:", customerInfo);

      const successMessage = isQuotation
        ? "Quotation will be sent via email once booking is complete..."
        : "Rooms are available, Redirecting to reservation page...";

      toast.success(successMessage, { duration: 5000 });

      navigate("/reservation", {
        state: {
          rates: rates,
          reservationData: reservationData,
        },
      });
    } catch (error) {
      console.error("Error checking availability:", error);
      toast.error("Failed to check availability. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (selectedOutletId) {
      // Get company ID if customer is an existing company
      const companyID =
        customerInfo?.isExisting && customerInfo?.customerType === "company"
          ? customerInfo?.customerData?.companyID
          : null;

      console.log("Fetching accommodation details with companyID:", companyID);

      // Always use getProductOrServiceDetails (with or without companyID)
      getProductOrServiceDetails("Accommodation", selectedOutletId, companyID)
        .then((accommodationDetails) => {
          console.log("Accommodation details response:", accommodationDetails);
          setAccommodationDetails(accommodationDetails);

          // Update options for dropdown from accommodation details
          if (accommodationDetails && accommodationDetails.length > 0) {
            // Transform the accommodation details into options format
            const roomOptions = [];

            // Loop through each accommodation item
            accommodationDetails.forEach((item) => {
              // Each item has childRecords array with the actual room types
              if (item.childRecords && item.childRecords.length > 0) {
                item.childRecords.forEach((child) => {
                  roomOptions.push({
                    typeName: child.typeName, // "Steinmuller DBB"
                    parentId: child.parentId, // 219
                    childId: child.childId, // 1984
                    typeId: child.typeId, // 1024
                    rate: child.rate, // 440.00
                  });
                });
              }
            });

            setOptions(roomOptions);
            console.log("Set room options:", roomOptions);
          } else {
            console.log("No accommodation details returned");
            setOptions([]);
          }
        })
        .catch((error) => {
          console.error("Error fetching accommodation details:", error);
          setOptions([]);
        });
    }
  }, [selectedOutletId, customerInfo]);

  const handleChange = (value, roomType, index, category) => {
    const selectedOption = options.find((option) => option.typeName === value);

    if (!selectedOption) return;

    const roomCategory = roomType.category;
    const roomPackage = selectedOption?.typeName;
    const dropDownIndex = roomType.index;

    setRates((prev) => {
      // FIXED: Store directly in rates without category nesting
      const updatedRates = { ...prev };

      // Remove any existing entry with the same dropdown index
      Object.keys(updatedRates).forEach((key) => {
        if (updatedRates[key]?.index === dropDownIndex) {
          delete updatedRates[key];
        }
      });

      // Create unique key using package name and index
      const uniqueKey = `${roomPackage}_${dropDownIndex}`;

      // Add new selection directly to rates
      updatedRates[uniqueKey] = {
        index: dropDownIndex,
        parentId: selectedOption.parentId,
        childId: selectedOption.childId,
        typeId: selectedOption.typeId,
        price: selectedOption.rate,
        category: roomCategory, // Store category for reference
        typeName: roomPackage, // Store the original type name
      };

      console.log("Updated rates (flat structure):", updatedRates);
      return updatedRates;
    });

    handleRoomTypeChange(value, roomCategory, index);
  };

  // Handle booking type selection
  const handleBookingTypeSelect = (type) => {
    setBookingType(type);
    setActiveTab("customerSelection");
    setCustomerInfo(null);
  };

  // Handle returning to home page when account is on hold
  const handleReturnToHome = () => {
    onClose(); // Close the modal
    navigate("/"); // Navigate to home page
  };

  // If account is on hold, show warning screen
  if (accountOnHold) {
    return (
      <ModalContainer>
        <ModalContentWrapper>
          <ModalHeader>
            <LogoContainer>
              <Logo src="/logo.png" alt="Logo" />
            </LogoContainer>
            <CloseButton onClick={onClose}>×</CloseButton>
          </ModalHeader>

          <div className="account-hold-warning">
            <div className="warning-container">
              <div className="warning-icon">
                <CiWarning size={80} />
              </div>
              <h2 className="warning-title">Account On Hold</h2>
              <p className="warning-message">
                Your account is currently on hold. You cannot make reservations
                at this time.
              </p>
              <p className="warning-contact">
                Please contact <strong>Isiqalo Guesthouse</strong> to resolve
                this issue before proceeding with your booking.
              </p>
              <div className="warning-actions">
                <Button
                  type="primary"
                  size="large"
                  onClick={handleReturnToHome}
                  className="return-home-btn"
                >
                  Return to Home Page
                </Button>
              </div>
            </div>
          </div>
        </ModalContentWrapper>
      </ModalContainer>
    );
  }

  return (
    <ModalContainer>
      <ModalContentWrapper>
        <ModalHeader>
          <LogoContainer>
            <Logo src="/logo.png" alt="Logo" />
          </LogoContainer>
          <CloseButton onClick={onClose}>Cancel</CloseButton>
        </ModalHeader>

        <TabsContainer>
          <TabsWrapper>
            <Tabs>
              {getTabsForBookingType().map((tab) => (
                <Tab
                  key={tab.key}
                  active={activeTab === tab.key}
                  onClick={() => setActiveTab(tab.key)}
                  disabled={tab.disabled}
                >
                  {tab.label}
                  {tab.info && <TabInfo>{tab.info}</TabInfo>}
                </Tab>
              ))}
            </Tabs>
          </TabsWrapper>
        </TabsContainer>

        <ModalContent>
          {activeTab === "creditApplication" && (
            <div className="credit-application-content">
              <div className="credit-application-header">
                <h2
                  style={{
                    textAlign: "center",
                    color: "#2c3e50",
                    marginBottom: "1rem",
                    fontSize: "2rem",
                  }}
                >
                  Book Now, Pay Later!
                </h2>
                <p
                  style={{
                    textAlign: "center",
                    color: "#6c757d",
                    fontSize: "1.1rem",
                    marginBottom: "2rem",
                    maxWidth: "600px",
                    margin: "0 auto 2rem",
                  }}
                >
                  Stay with us today and enjoy flexible payment options. Apply
                  for credit and experience luxury accommodation while managing
                  your cash flow.
                </p>
              </div>

              <div
                className="credit-application-benefits"
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(auto-fit, minmax(250px, 1fr))",
                  gap: "1.5rem",
                  marginBottom: "3rem",
                }}
              >
                <div
                  style={{
                    textAlign: "center",
                    padding: "1.5rem",
                    backgroundColor: "#f8f9fa",
                    borderRadius: "12px",
                    border: "1px solid #e9ecef",
                  }}
                >
                  <CheckCircleOutlined
                    style={{
                      fontSize: "2.5rem",
                      color: "#084c7c",
                      marginBottom: "1rem",
                    }}
                  />
                  <h3 style={{ color: "#2c3e50", marginBottom: "0.5rem" }}>
                    Quick Approval
                  </h3>
                  <p style={{ color: "#6c757d", margin: "0" }}>
                    Fast processing with decisions typically within 24 hours
                  </p>
                </div>
                <div
                  style={{
                    textAlign: "center",
                    padding: "1.5rem",
                    backgroundColor: "#f8f9fa",
                    borderRadius: "12px",
                    border: "1px solid #e9ecef",
                  }}
                >
                  <CalendarOutlined
                    style={{
                      fontSize: "2.5rem",
                      color: "#084c7c",
                      marginBottom: "1rem",
                    }}
                  />
                  <h3 style={{ color: "#2c3e50", marginBottom: "0.5rem" }}>
                    Flexible Terms
                  </h3>
                  <p style={{ color: "#6c757d", margin: "0" }}>
                    Customizable payment schedules that work for your budget
                  </p>
                </div>
                <div
                  style={{
                    textAlign: "center",
                    padding: "1.5rem",
                    backgroundColor: "#f8f9fa",
                    borderRadius: "12px",
                    border: "1px solid #e9ecef",
                  }}
                >
                  <InfoCircleOutlined
                    style={{
                      fontSize: "2.5rem",
                      color: "#084c7c",
                      marginBottom: "1rem",
                    }}
                  />
                  <h3 style={{ color: "#2c3e50", marginBottom: "0.5rem" }}>
                    Secure Process
                  </h3>
                  <p style={{ color: "#6c757d", margin: "0" }}>
                    Bank-level security and confidential application processing
                  </p>
                </div>
              </div>

              <div
                className="credit-application-download"
                style={{
                  textAlign: "center",
                  padding: "2rem",
                  backgroundColor: "#fff",
                  borderRadius: "12px",
                  border: "2px solid #e9ecef",
                  boxShadow: "0 4px 6px rgba(0,0,0,0.1)",
                }}
              >
                <h3
                  style={{
                    color: "#2c3e50",
                    marginBottom: "1rem",
                    fontSize: "1.5rem",
                  }}
                >
                  Ready to Apply?
                </h3>
                <p
                  style={{
                    fontSize: "1rem",
                    color: "#6c757d",
                    marginBottom: "2rem",
                    lineHeight: "1.6",
                  }}
                >
                  Download our credit application form below. Fill it out
                  completely and email it to <strong>credit@isiqalo.com</strong>{" "}
                  for processing.
                  <br />
                  <small style={{ color: "#868e96" }}>
                    All information is kept strictly confidential.
                  </small>
                </p>
                <a
                  href="/AcknowledgementOfDebt.docx"
                  download="Isiqalo_Credit_Application.docx"
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: "0.75rem",
                    backgroundColor: "#084c7c",
                    color: "white",
                    padding: "16px 32px",
                    borderRadius: "25px",
                    textDecoration: "none",
                    fontSize: "1rem",
                    fontWeight: "500",
                    letterSpacing: "0.05em",
                    transition: "all 0.3s ease",
                    border: "none",
                    cursor: "pointer",
                    height: "50px",
                    marginBottom: "1.5rem",
                    fontFamily: '"Montserrat", sans-serif',
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.backgroundColor = "#063c62";
                    e.target.style.transform = "translateY(-2px)";
                    e.target.style.boxShadow = "0 4px 8px rgba(0, 0, 0, 0.1)";
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.backgroundColor = "#084c7c";
                    e.target.style.transform = "translateY(0)";
                    e.target.style.boxShadow = "none";
                  }}
                >
                  <svg
                    width="16"
                    height="16"
                    fill="currentColor"
                    viewBox="0 0 16 16"
                  >
                    <path d="M.5 9.9a.5.5 0 0 1 .5.5v2.5a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1v-2.5a.5.5 0 0 1 1 0v2.5a2 2 0 0 1-2 2H2a2 2 0 0 1-2-2v-2.5a.5.5 0 0 1 .5-.5z" />
                    <path d="M7.646 11.854a.5.5 0 0 0 .708 0l3-3a.5.5 0 0 0-.708-.708L8.5 10.293V1.5a.5.5 0 0 0-1 0v8.793L5.354 8.146a.5.5 0 1 0-.708.708l3 3z" />
                  </svg>
                  Download Credit Application
                </a>

                <div
                  style={{
                    marginTop: "2rem",
                    padding: "1rem 0",
                    borderTop: "1px solid #e9ecef",
                  }}
                >
                  <Button
                    type="primary"
                    size="large"
                    onClick={() => setActiveTab("bookingType")}
                    style={{
                      backgroundColor: "#084c7c",
                      borderColor: "#084c7c",
                      height: "50px",
                      borderRadius: "25px",
                      fontSize: "1rem",
                      fontWeight: "500",
                      letterSpacing: "0.05em",
                      fontFamily: '"Montserrat", sans-serif',
                    }}
                    onMouseEnter={(e) => {
                      e.target.style.backgroundColor = "#063c62";
                      e.target.style.transform = "translateY(-2px)";
                      e.target.style.boxShadow = "0 4px 8px rgba(0, 0, 0, 0.1)";
                    }}
                    onMouseLeave={(e) => {
                      e.target.style.backgroundColor = "#084c7c";
                      e.target.style.transform = "translateY(0)";
                      e.target.style.boxShadow = "none";
                    }}
                  >
                    Continue to Regular Booking →
                  </Button>
                  <p
                    style={{
                      fontSize: "0.9rem",
                      color: "#868e96",
                      marginTop: "0.5rem",
                      marginBottom: "0",
                    }}
                  >
                    Not interested in credit? Continue with regular booking
                    process. By continuing, you agree to our{" "}
                    <TermsAndConditions /> which govern your stay.
                  </p>
                </div>
              </div>
            </div>
          )}

          {activeTab === "bookingType" && (
            <div className="booking-type-selection">
              <div className="booking-type-header">
                <h2 className="booking-type-main-title">Select Booking Type</h2>
                <p className="booking-type-main-subtitle">
                  Choose how you'd like to make your reservation
                </p>
              </div>

              <div className="booking-type-cards">
                <div
                  className={`booking-type-card ${
                    bookingType === "individual" ? "selected" : ""
                  }`}
                  onClick={() => handleBookingTypeSelect("individual")}
                >
                  <div className="booking-type-card-icon">
                    <UserOutlined />
                  </div>
                  <h3>Individual Booking</h3>
                  <p>
                    Perfect for personal stays, family trips, or small groups
                  </p>
                  <ul>
                    <li>Single check-in/check-out dates</li>
                    <li>Simplified booking process</li>
                    <li>Immediate confirmation</li>
                  </ul>
                </div>

                <div
                  className={`booking-type-card ${
                    bookingType === "company" ? "selected" : ""
                  }`}
                  onClick={() => handleBookingTypeSelect("company")}
                >
                  <div className="booking-type-card-icon">
                    <svg
                      width="24"
                      height="24"
                      viewBox="0 0 24 24"
                      fill="currentColor"
                    >
                      <path d="M12 7V3H2v18h20V7H12zM6 19H4v-2h2v2zm0-4H4v-2h2v2zm0-4H4V9h2v2zm0-4H4V5h2v2zm4 12H8v-2h2v2zm0-4H8v-2h2v2zm0-4H8V9h2v2zm0-4H8V5h2v2zm10 12h-8v-2h2v-2h-2v-2h2v-2h-2V9h8v10zm-2-8h-2v2h2v-2zm0 4h-2v2h2v-2z" />
                    </svg>
                  </div>
                  <h3>Company Booking</h3>
                  <p>Ideal for business travel and corporate accommodations</p>
                  <ul>
                    <li>Individual dates per room</li>
                    <li>Multiple guest management</li>
                    <li>Flexible scheduling</li>
                  </ul>
                </div>
              </div>
            </div>
          )}
          {activeTab === "customerSelection" && (
            <CustomerSelection
              onCustomerSelected={handleCustomerSelected}
              bookingType={bookingType}
            />
          )}
          {activeTab === "travelDates" && bookingType === "individual" && (
            <TravelDatesSection>
              <CalendarContainer>
                <CalendarNavigation>
                  <NavButton onClick={goToPreviousMonth}>
                    <LeftOutlined />
                  </NavButton>
                  <CalendarMonthsWrapper>
                    <CalendarMonth>
                      <MonthHeader>
                        {currentMonthDate.format("MMMM YYYY")}
                      </MonthHeader>
                      <WeekdayHeader>
                        <WeekdayCell>SU</WeekdayCell>
                        <WeekdayCell>MO</WeekdayCell>
                        <WeekdayCell>TU</WeekdayCell>
                        <WeekdayCell>WE</WeekdayCell>
                        <WeekdayCell>TH</WeekdayCell>
                        <WeekdayCell>FR</WeekdayCell>
                        <WeekdayCell>SA</WeekdayCell>
                      </WeekdayHeader>
                      <CalendarGrid>
                        {generateCalendarDays(currentMonthDate).map(
                          (day, index) => (
                            <CalendarCell
                              key={index}
                              onClick={() =>
                                day &&
                                !isDateDisabled(day) &&
                                handleDateClick(day)
                              }
                              onMouseEnter={() =>
                                day &&
                                !isDateDisabled(day) &&
                                handleDateHover(day)
                              }
                              disabled={day && isDateDisabled(day)}
                              isStart={
                                day && startDate && day.isSame(startDate, "day")
                              }
                              isEnd={
                                day && endDate && day.isSame(endDate, "day")
                              }
                              isInRange={day && isInSelectedRange(day)}
                              isHoverRange={day && isInHoverRange(day)}
                              isEmpty={!day}
                            >
                              {day && day.date()}
                            </CalendarCell>
                          )
                        )}
                      </CalendarGrid>
                    </CalendarMonth>

                    <CalendarMonth>
                      <MonthHeader>
                        {currentMonthDate.add(1, "month").format("MMMM YYYY")}
                      </MonthHeader>
                      <WeekdayHeader>
                        <WeekdayCell>SU</WeekdayCell>
                        <WeekdayCell>MO</WeekdayCell>
                        <WeekdayCell>TU</WeekdayCell>
                        <WeekdayCell>WE</WeekdayCell>
                        <WeekdayCell>TH</WeekdayCell>
                        <WeekdayCell>FR</WeekdayCell>
                        <WeekdayCell>SA</WeekdayCell>
                      </WeekdayHeader>
                      <CalendarGrid>
                        {generateCalendarDays(
                          currentMonthDate.add(1, "month")
                        ).map((day, index) => (
                          <CalendarCell
                            key={index}
                            onClick={() =>
                              day &&
                              !isDateDisabled(day) &&
                              handleDateClick(day)
                            }
                            onMouseEnter={() =>
                              day &&
                              !isDateDisabled(day) &&
                              handleDateHover(day)
                            }
                            disabled={day && isDateDisabled(day)}
                            isStart={
                              day && startDate && day.isSame(startDate, "day")
                            }
                            isEnd={day && endDate && day.isSame(endDate, "day")}
                            isInRange={day && isInSelectedRange(day)}
                            isHoverRange={day && isInHoverRange(day)}
                            isEmpty={!day}
                          >
                            {day && day.date()}
                          </CalendarCell>
                        ))}
                      </CalendarGrid>
                    </CalendarMonth>
                  </CalendarMonthsWrapper>
                  <NavButton onClick={goToNextMonth}>
                    <RightOutlined />
                  </NavButton>
                </CalendarNavigation>

                {startDate && endDate && (
                  <DateSelectionInfo>
                    Selected: {startDate.format("MMM DD, YYYY")} -{" "}
                    {endDate.format("MMM DD, YYYY")} (
                    {endDate.diff(startDate, "day")} nights)
                  </DateSelectionInfo>
                )}
              </CalendarContainer>
            </TravelDatesSection>
          )}

          {activeTab === "roomsGuests" && (
            <>
              {/* Room and Guest Selection Controls */}
              <RoomSelection>
                <RoomSelectionTitle>Rooms & Guests</RoomSelectionTitle>
                <RoomSelectionSubtitle>
                  How many guests & rooms for your stay?
                </RoomSelectionSubtitle>

                <SelectionGrid>
                  <SelectionColumn>
                    <SelectionLabel>Guest(s)</SelectionLabel>
                    <SelectionControls>
                      <StepperButton onClick={() => updateAdults(adults - 1)}>
                        -
                      </StepperButton>
                      <SelectionValue>
                        {String(adults).padStart(2, "0")}
                      </SelectionValue>
                      <StepperButton onClick={() => updateAdults(adults + 1)}>
                        +
                      </StepperButton>
                    </SelectionControls>
                  </SelectionColumn>

                  <SelectionColumn>
                    <SelectionLabel>Room(s)</SelectionLabel>
                    <SelectionControls>
                      <StepperButton onClick={() => updateRooms(rooms - 1)}>
                        -
                      </StepperButton>
                      <SelectionValue>
                        {String(rooms).padStart(2, "0")}
                      </SelectionValue>
                      <StepperButton onClick={() => updateRooms(rooms + 1)}>
                        +
                      </StepperButton>
                    </SelectionControls>
                  </SelectionColumn>
                </SelectionGrid>
              </RoomSelection>

              {/* Room Selection Section */}
              <RoomSelectionSection>
                <SectionTitle1>
                  Select Your Rooms
                  <RoomSelectionCounter>
                    {totalSelectedRooms} of {rooms} selected
                    <Tooltip
                      title={`You must select exactly ${rooms} room(s) and choose room types to proceed`}
                    >
                      <InfoIcon />
                    </Tooltip>
                  </RoomSelectionCounter>
                </SectionTitle1>

                <RoomList>
                  {/* Standard Room */}
                  <RoomCardNew>
                    <RoomImageCol>
                      <RoomImg
                        src="/standardRoom.jpg"
                        alt="Standard Room"
                        style={{ width: "520px", height: "420px" }}
                      />
                    </RoomImageCol>

                    <RoomDetailsCol>
                      <RoomHeaderRow>
                        <RoomTypeHeader>Standard Room</RoomTypeHeader>
                        <RoomSelectionControls>
                          <RoomCounter>
                            <StepperButton
                              onClick={() => {
                                if (selectedRooms.standard > 0) {
                                  handleRoomSelectionChange("standard", false);
                                }
                              }}
                              disabled={selectedRooms.standard === 0}
                            >
                              -
                            </StepperButton>
                            <CounterValue>
                              {selectedRooms.standard}
                            </CounterValue>
                            <StepperButton
                              onClick={() =>
                                handleRoomSelectionChange("standard", true)
                              }
                              disabled={totalSelectedRooms >= rooms}
                            >
                              +
                            </StepperButton>
                          </RoomCounter>
                        </RoomSelectionControls>
                      </RoomHeaderRow>

                      <RoomDescription>
                        Each of our rooms has its own unique sense of style and
                        design. No two rooms are the same, but each was
                        carefully designed with a special visual aesthetic in
                        mind. We are confident that you will love what you see.
                        Our comfortable, spacious bedrooms have en-suite
                        bathrooms.
                      </RoomDescription>

                      <AmenitiesGrid>
                        <AmenityItem>
                          <CheckIcon /> Room Service
                        </AmenityItem>
                        <AmenityItem>
                          <CheckIcon /> Wi-fi Service
                        </AmenityItem>
                        <AmenityItem>
                          <CheckIcon /> DSTV
                        </AmenityItem>
                        <AmenityItem>
                          <CheckIcon /> Heater
                        </AmenityItem>
                        <AmenityItem>
                          <CheckIcon /> Hair Dryer
                        </AmenityItem>
                        <AmenityItem>
                          <CheckIcon /> Refrigerators
                        </AmenityItem>
                        <AmenityItem>
                          <CheckIcon /> Electric Blankets
                        </AmenityItem>
                        <AmenityItem>
                          <CheckIcon /> Tea Trays
                        </AmenityItem>
                        <AmenityItem>
                          <CheckIcon /> Laundry Service
                        </AmenityItem>
                        <AmenityItem>
                          <CheckIcon /> Fresh Rusks
                        </AmenityItem>
                      </AmenitiesGrid>

                      {/* Room Type Selection Dropdowns */}
                      {selectedRooms.standard > 0 && (
                        <RoomTypesSection>
                          <RoomTypesList>
                            {Array.from({ length: selectedRooms.standard }).map(
                              (_, index) => (
                                <RoomTypeRow key={index}>
                                  <RoomTypeNumber>
                                    Stay Option {index + 1}
                                  </RoomTypeNumber>
                                  <RoomTypeSelect>
                                    <Select
                                      placeholder="Please select Room Type"
                                      style={{ width: "100%" }}
                                      onChange={(value) =>
                                        handleChange(
                                          value,
                                          { category: "standard", index },
                                          index,
                                          selectedOutletId
                                        )
                                      }
                                    >
                                      <Option value="" disabled>
                                        Select a Room Type
                                      </Option>
                                      {options.map((option) => (
                                        <Option
                                          key={option.typeName}
                                          value={option.typeName}
                                          index={index + 1}
                                          category="standard"
                                        >
                                          {option.typeName}
                                        </Option>
                                      ))}
                                    </Select>
                                  </RoomTypeSelect>
                                </RoomTypeRow>
                              )
                            )}
                          </RoomTypesList>
                        </RoomTypesSection>
                      )}
                    </RoomDetailsCol>
                  </RoomCardNew>

                  {/* Self-Catering Room */}
                  <RoomCardNew>
                    <RoomImageCol>
                      <RoomImg
                        src="/self-catering.jpg"
                        alt="Self-Catering Room"
                        style={{ width: "520px", height: "420px" }}
                      />
                    </RoomImageCol>

                    <RoomDetailsCol>
                      <RoomHeaderRow>
                        <RoomTypeHeader>Self-Catering Room</RoomTypeHeader>
                        <RoomSelectionControls>
                          <RoomCounter>
                            <StepperButton
                              onClick={() => {
                                if (selectedRooms.selfCatering > 0) {
                                  handleRoomSelectionChange(
                                    "selfCatering",
                                    false
                                  );
                                }
                              }}
                              disabled={selectedRooms.selfCatering === 0}
                            >
                              -
                            </StepperButton>
                            <CounterValue>
                              {selectedRooms.selfCatering}
                            </CounterValue>
                            <StepperButton
                              onClick={() =>
                                handleRoomSelectionChange("selfCatering", true)
                              }
                              disabled={totalSelectedRooms >= rooms}
                            >
                              +
                            </StepperButton>
                          </RoomCounter>
                        </RoomSelectionControls>
                      </RoomHeaderRow>

                      <RoomDescription>
                        We have 3 bright and airy self-catering rooms, each
                        decorated in a modern style. Suited for couples /
                        individuals or those with a small child. A fold-out bed
                        for a small child is supplied on request. Our rates are
                        inexpensive to meet your budget, but the quality is not
                        compromised.
                      </RoomDescription>

                      <AmenitiesGrid>
                        <AmenityItem>
                          <CheckIcon /> Fully equipped Kitchen
                        </AmenityItem>
                        <AmenityItem>
                          <CheckIcon /> Wi-fi Service
                        </AmenityItem>
                        <AmenityItem>
                          <CheckIcon /> DSTV
                        </AmenityItem>
                        <AmenityItem>
                          <CheckIcon /> Heater
                        </AmenityItem>
                        <AmenityItem>
                          <CheckIcon /> Porch
                        </AmenityItem>
                        <AmenityItem>
                          <CheckIcon /> Refrigerator & Freezer
                        </AmenityItem>
                        <AmenityItem>
                          <CheckIcon /> Braai Area
                        </AmenityItem>
                        <AmenityItem>
                          <CheckIcon /> Microwave
                        </AmenityItem>
                        <AmenityItem>
                          <CheckIcon /> Laundry Service
                        </AmenityItem>
                        <AmenityItem>
                          <CheckIcon /> Fresh Rusks
                        </AmenityItem>
                      </AmenitiesGrid>

                      {/* Room Type Selection Dropdowns */}
                      {selectedRooms.selfCatering > 0 && (
                        <RoomTypesSection>
                          <RoomTypesList>
                            {Array.from({
                              length: selectedRooms.selfCatering,
                            }).map((_, index) => (
                              <RoomTypeRow key={index}>
                                <RoomTypeNumber>
                                  Room {index + 1}
                                </RoomTypeNumber>
                                <RoomTypeSelect>
                                  <Select
                                    placeholder="Please select Room Type"
                                    style={{ width: "100%" }}
                                    onChange={(value) =>
                                      handleChange(
                                        value,
                                        { category: "selfCatering", index },
                                        index,
                                        selectedOutletId
                                      )
                                    }
                                  >
                                    {options.map((option) => (
                                      <Option
                                        key={option.typeName}
                                        value={option.typeName}
                                        index={index + 1}
                                        category="selfCatering"
                                      >
                                        {option.typeName}
                                      </Option>
                                    ))}
                                  </Select>
                                </RoomTypeSelect>
                              </RoomTypeRow>
                            ))}
                          </RoomTypesList>
                        </RoomTypesSection>
                      )}
                    </RoomDetailsCol>
                  </RoomCardNew>

                  {/* Disability-Friendly Room */}
                  <RoomCardNew>
                    <RoomImageCol>
                      <RoomImg
                        src="/disability_room.jpg"
                        alt="Disability-Friendly Room"
                        style={{ width: "520px", height: "420px" }}
                      />
                    </RoomImageCol>

                    <RoomDetailsCol>
                      <RoomHeaderRow>
                        <RoomTypeHeader>
                          Disability-Friendly Rooms
                        </RoomTypeHeader>
                        <RoomSelectionControls>
                          <RoomCounter>
                            <StepperButton
                              onClick={() => {
                                if (selectedRooms.disabilityFriendly > 0) {
                                  handleRoomSelectionChange(
                                    "disabilityFriendly",
                                    false
                                  );
                                }
                              }}
                              disabled={selectedRooms.disabilityFriendly === 0}
                            >
                              -
                            </StepperButton>
                            <CounterValue>
                              {selectedRooms.disabilityFriendly}
                            </CounterValue>
                            <StepperButton
                              onClick={() =>
                                handleRoomSelectionChange(
                                  "disabilityFriendly",
                                  true
                                )
                              }
                              disabled={totalSelectedRooms >= rooms}
                            >
                              +
                            </StepperButton>
                          </RoomCounter>
                        </RoomSelectionControls>
                      </RoomHeaderRow>

                      <RoomDescription>
                        Our disability-friendly room has been designed for our
                        physically challenged guests. It is well appointed and
                        has a comfortable King size bed or can be converted into
                        twin single beds. The bathroom is spacious with a
                        wheelchair-friendly shower. The room opens up onto a
                        porch with a wheelchair ramp and wheelchair-friendly
                        parking. The room is also available as a self-catering
                        room.
                      </RoomDescription>

                      <AmenitiesGrid>
                        <AmenityItem>
                          <CheckIcon /> Room Service
                        </AmenityItem>
                        <AmenityItem>
                          <CheckIcon /> Wi-fi Service
                        </AmenityItem>
                        <AmenityItem>
                          <CheckIcon /> DSTV
                        </AmenityItem>
                        <AmenityItem>
                          <CheckIcon /> Heater
                        </AmenityItem>
                        <AmenityItem>
                          <CheckIcon /> Hair Dryer
                        </AmenityItem>
                        <AmenityItem>
                          <CheckIcon /> Refrigerators
                        </AmenityItem>
                        <AmenityItem>
                          <CheckIcon /> Electric Blankets
                        </AmenityItem>
                        <AmenityItem>
                          <CheckIcon /> Tea Trays
                        </AmenityItem>
                        <AmenityItem>
                          <CheckIcon /> Laundry Service
                        </AmenityItem>
                        <AmenityItem>
                          <CheckIcon /> Fresh Rusks
                        </AmenityItem>
                      </AmenitiesGrid>

                      {selectedRooms.disabilityFriendly > 0 && (
                        <RoomTypesSection>
                          <RoomTypesList>
                            {Array.from({
                              length: selectedRooms.disabilityFriendly,
                            }).map((_, index) => (
                              <RoomTypeRow key={index}>
                                <RoomTypeNumber>
                                  Room {index + 1}
                                </RoomTypeNumber>
                                <RoomTypeSelect>
                                  <Select
                                    placeholder="Please select Room Type"
                                    style={{ width: "100%" }}
                                    onChange={(value) =>
                                      handleChange(
                                        value,
                                        {
                                          category: "disabilityFriendly",
                                          index,
                                        },
                                        index,
                                        selectedOutletId
                                      )
                                    }
                                  >
                                    {options.map((option) => (
                                      <Option
                                        key={option.typeName}
                                        value={option.typeName}
                                        index={index + 1}
                                        category="disabilityFriendly"
                                      >
                                        {option.typeName}
                                      </Option>
                                    ))}
                                  </Select>
                                </RoomTypeSelect>
                              </RoomTypeRow>
                            ))}
                          </RoomTypesList>
                        </RoomTypesSection>
                      )}
                    </RoomDetailsCol>
                  </RoomCardNew>
                </RoomList>
              </RoomSelectionSection>

              {/* Continue to Guest Details Button */}
              <CTAGroup>
                <StyledButton
                  type="primary"
                  block
                  size="large"
                  disabled={!areAllRoomTypesSelected()}
                  onClick={() => setActiveTab("guestDetails")}
                >
                  {bookingType === "individual"
                    ? "Continue to Booking Options"
                    : "Continue to Guest Details"}
                </StyledButton>
                {!areAllRoomTypesSelected() && (
                  <SelectionWarning>
                    Please select exactly {rooms} room(s) and choose room types
                    for each to continue
                  </SelectionWarning>
                )}
              </CTAGroup>
            </>
          )}

          {activeTab === "guestDetails" && (
            <div className="guest-details-section">
              <div className="guest-details-header">
                <h2 className="guest-details-title">
                  {bookingType === "individual"
                    ? "Booking Options"
                    : "Guest Details & Check-in Dates"}
                </h2>
                <p className="guest-details-subtitle">
                  {bookingType === "individual"
                    ? "Choose your booking preferences and proceed"
                    : "Please provide guest information and individual dates for each room"}
                </p>
              </div>

              {/* Quotation Toggle */}
              <div className="quotation-section">
                <div className="quotation-toggle">
                  <span className="Quotation">Request as Quotation</span>
                  <Switch
                    checked={isQuotation}
                    onChange={setIsQuotation}
                    className="quotation-switch"
                  />
                </div>
                <div className="quotation-note">
                  {isQuotation
                    ? "You will receive a quotation that you can review before confirming"
                    : "Proceed directly to booking confirmation"}
                </div>
              </div>

              {/* Guest Details Forms - Only show for company bookings */}
              {bookingType === "company" && (
                <div className="guest-details-container">
                  {guestDetails.map((guest, index) => (
                    <div key={index} className="guest-card company-guest-card">
                      <div className="guest-card-header">
                        <UserOutlined className="guest-icon" />
                        <span>Room {index + 1} - Guest Details</span>
                      </div>

                      <div className="guest-form">
                        <div className="form-row">
                          <div className="form-field">
                            <label>Full Name *</label>
                            <Input
                              placeholder="Enter full name"
                              value={guest.fullName}
                              onChange={(e) =>
                                handleGuestDetailChange(
                                  index,
                                  "fullName",
                                  e.target.value
                                )
                              }
                              prefix={<UserOutlined />}
                            />
                          </div>
                          <div className="form-field">
                            <label>Email Address *</label>
                            <Input
                              placeholder="Enter email address"
                              type="email"
                              value={guest.email}
                              onChange={(e) =>
                                handleGuestDetailChange(
                                  index,
                                  "email",
                                  e.target.value
                                )
                              }
                              prefix={<MailOutlined />}
                            />
                          </div>
                        </div>

                        <div className="form-row">
                          <div className="form-field full-width">
                            <label>Check-in & Check-out Dates *</label>
                            <RangePicker
                              style={{ width: "100%" }}
                              placeholder={["Check-in Date", "Check-out Date"]}
                              format="YYYY-MM-DD"
                              disabledDate={(current) =>
                                current && current < dayjs().startOf("day")
                              }
                              value={
                                guest.checkInDate && guest.checkOutDate
                                  ? [guest.checkInDate, guest.checkOutDate]
                                  : null
                              }
                              onChange={(dates) =>
                                handleGuestDateChange(index, dates)
                              }
                              prefix={<CalendarOutlined />}
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* Individual Booking Notice */}
              {bookingType === "individual" && (
                <div className="individual-booking-notice">
                  <div className="notice-card">
                    <UserOutlined className="notice-icon" />
                    <div className="notice-content">
                      <h4>Individual Booking Selected</h4>
                      <p>
                        For individual bookings, guest details will be collected
                        during the reservation process. You can proceed directly
                        to check availability.
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {/* Final CTA */}
              <CTAGroup>
                <StyledButton
                  type="primary"
                  block
                  size="large"
                  loading={loading}
                  onClick={handleCheckAvailability}
                  disabled={!areGuestDetailsComplete()}
                >
                  {isQuotation
                    ? "Request Quotation"
                    : "Check Availability & Book"}
                </StyledButton>
                {bookingType === "company" && !areGuestDetailsComplete() && (
                  <SelectionWarning>
                    Please fill in all guest details and dates to continue
                  </SelectionWarning>
                )}
              </CTAGroup>
            </div>
          )}
        </ModalContent>
      </ModalContentWrapper>
    </ModalContainer>
  );
};

const DestinationSection = styled.div`
  padding: 2rem 0;
`;

const SearchContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
`;

const SearchBarContainer = styled.div`
  display: flex;
  flex-direction: column;
  margin-bottom: 1rem;
  width: 50%;
  margin: 0 auto;
`;

const SearchHeader = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
`;

const HeaderTitle = styled.h5`
  margin: 0;
  font-size: 0.8rem;
  font-weight: 500;
  color: #333;
`;

const SelectionType = styled.h4`
  margin: 0;
  font-size: 1.25rem;
  font-weight: 600;
  color: #222;
  margin-bottom: 0.5rem;
`;

const SearchInputWrapper = styled.div`
  position: relative;
  border-bottom: 1px solid #ddd;
  padding-bottom: 0.5rem;
  display: flex;
  align-items: center;
`;

const SearchInput = styled.input`
  border: none;
  outline: none;
  width: 100%;
  padding: 0.5rem 0;
  font-size: 1rem;
  color: #333;

  &::placeholder {
    color: #aaa;
  }

  &:focus {
    border-color: #084c7c;
  }
`;

const SearchIcon = styled.span`
  color: #084c7c;
  font-size: 1.25rem;
  margin-left: 0.5rem;
`;

const SearchContent = styled.div`
  display: flex;
  gap: 2rem;
  position: relative;
  padding: 0.5rem;
  margin-top: 1rem;
`;

const Section = styled.div`
  flex: 1;
  padding-right: 2rem;
  border-right: ${(props) => (props.hasDivider ? "1px solid #eee" : "none")};
`;

const SectionTitle = styled.h5`
  font-size: 1rem;
  margin-bottom: 1rem;
  color: #333;
  font-weight: 500;
`;

const SearchItem = styled.div`
  display: flex;
  flex-direction: column;
  margin-bottom: 0.75rem;
  cursor: pointer;
  transition: all 0.2s;
  color: ${(props) => (props.isSelected ? "#084c7c" : "inherit")};
  font-weight: ${(props) => (props.isSelected ? "500" : "normal")};

  &:hover {
    color: #084c7c;
  }
`;

const ItemName = styled.span`
  margin-bottom: 0.2rem;
`;

const ItemCountWrapper = styled.div`
  display: flex;
`;

const ItemCount = styled.span`
  color: #888;
  font-size: 0.85rem;
`;

const ModalContainer = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: stretch;
  justify-content: stretch;
  z-index: 1001;
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 2rem;
`;

const LogoContainer = styled.div`
  display: flex;
  align-items: center;
`;

const Logo = styled.img`
  height: 50px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: color 0.2s;
  position: relative;
  top: -15px;
  right: -10px;
  margin: 0;
`;

const Logo2 = styled.img`
  height: 50px;
  cursor: pointer;
  margin-left: 10px;
`;

const CloseButton = styled.button`
  border-radius: 5px;
  border: none;
  font-size: 1.1rem;
  cursor: pointer;
  color: #000000;
  background: #dedede;
  padding: 8px 16px;
  width: auto;
  min-width: 70px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s;
  position: relative;
  top: -15px;
  right: 5px;
  margin: 0;
  line-height: 1;

  &:hover {
    color: red;
    background: rgba(255, 0, 0, 0.1);
  }
`;

const TabsContainer = styled.div`
  position: relative;
  top: -4%;
  width: 100vw;
  margin-left: -2rem;
  margin-right: -2rem;
  display: flex;
  flex-direction: column;
  align-items: stretch;
`;

const TabsWrapper = styled.div`
  position: relative;
  width: 100%;
  border-top: 1px solid black;
  border-left: 1px solid black;
  border-right: 1px solid black;
`;

const Tabs = styled.div`
  display: flex;
  position: relative;
`;

const Tab = styled.button`
  flex: 1;
  padding: 1rem;
  border: none;
  background: none;
  font-size: 1.1rem;
  font-weight: 500;
  color: ${(props) => (props.active ? "black" : "#665")};
  cursor: pointer;
  transition: all 0.2s;
  position: relative;
  opacity: ${(props) => (props.disabled ? "0.5" : "1")};
  cursor: ${(props) => (props.disabled ? "not-allowed" : "pointer")};

  &::after {
    content: "";
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
    height: 3px;
    background: ${(props) => (props.active ? "black" : "transparent")};
    transition: all 0.2s;
  }

  &:hover:not(:disabled) {
    color: #b99028;
  }
`;

const TabInfo = styled.div`
  font-size: 0.8rem;
  color: #666;
  margin-top: 0.5rem;
`;

const ModalContentWrapper = styled.div`
  background: white;
  width: 100%;
  height: 100%;
  padding: 2rem;
  box-shadow: none;
  border-radius: 0;
  overflow-x: hidden;
`;

const ModalContent = styled.div`
  padding: 1rem 0;
`;

const SelectionGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  width: 100%;
  gap: 1rem;
  border-top: 1px solid #eee;
`;

const SelectionColumn = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 2rem 1rem;
  position: relative;

  &:not(:last-child)::after {
    content: "";
    position: absolute;
    right: 0;
    top: 2rem;
    bottom: 2rem;
    width: 1px;
    background-color: #eee;
  }
`;

const SelectionLabel = styled.div`
  font-size: 1.2rem;
  font-weight: 500;
  margin-bottom: 1.5rem;
  color: #rgb(102, 102, 68);
`;

const SelectionControls = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  color: #000000;
`;

const DateSeparator = styled.span`
  color: #666;
`;

const RoomTypesSection = styled.div`
  width: 100%;
  margin-top: 2rem;
  padding-top: 2rem;
  border-top: 1px solid #eee;
`;

const RoomTypesSectionTitle = styled.h3`
  font-size: 1.4rem;
  font-weight: 500;
  margin-bottom: 1.5rem;
  text-align: center;
`;

const RoomTypeNumber = styled.div`
  font-weight: 500;
  margin-right: 1rem;
  min-width: 80px;
`;

const RoomTypeSelect = styled.div`
  flex-grow: 1;

  .ant-select {
    width: 80%;
    .ant-select-selector {
      border-color: #ddd;
      border-radius: 4px;
      transition: all 0.2s;
    }

    &:hover .ant-select-selector {
      border-color: #b99028;
    }
  }
`;

const RoomSelectionSection = styled.div`
  padding: 2rem;
`;

const RoomList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2rem;
`;

const CheckIcon = styled(CheckCircleOutlined)`
  color: #b99028;
  margin-right: 0.5rem;
`;

const BookNowButton = styled.button`
  position: absolute;
  bottom: 1.5rem;
  right: 1.5rem;
  background-color: #084c7c;
  color: white;
  border: none;
  padding: 0.75rem 1.5rem;
  font-weight: 600;
  letter-spacing: 1px;
  cursor: pointer;
  border-radius: 5px;
  transition: all 0.3s ease;

  &:hover {
    background-color: #b99028;
    color: white;
    transform: translateY(-2px);
  }
`;

const RoomCard = styled.div`
  display: flex;
  background-color: white;
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
`;

const RoomImage = styled.div`
  width: 300px;
  background-image: url("/api/placeholder/300/200");
  background-size: cover;
`;

const RoomDetails = styled.div`
  padding: 1.5rem;
  flex: 1;
`;

const RoomName = styled.h3`
  font-size: 1.3rem;
  font-weight: 500;
  margin: 0 0 1rem 0;
  color: #222;
`;

const BookButton = styled.button`
  background-color: #084c7c;
  color: white;
  border: none;
  padding: 0.75rem 1.5rem;
  border-radius: 4px;
  font-weight: 500;
  cursor: pointer;
  transition: background-color 0.2s;

  &:hover {
    background-color: #063c62;
  }
`;

const RoomSelectionCounter = styled.span`
  font-size: 1rem;
  font-weight: normal;
  color: #666;
  margin-left: 1rem;
  display: inline-flex;
  align-items: center;
`;

const InfoIcon = styled(InfoCircleOutlined)`
  margin-left: 0.5rem;
  color: #084c7c;
  cursor: help;
`;

const RoomHeaderRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1rem;
`;

const RoomSelectionControls = styled.div`
  display: flex;
  align-items: center;
`;

const RoomTypesList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
`;

const TravelDatesSection = styled.div`
  padding: 5px 14px;
  width: 100%;
`;

const CalendarContainer = styled.div`
  font-family: sans-serif;
  width: 90%;
  max-width: 90%;
  margin: 0 auto;

  @media (max-width: 1024px) {
    width: 95%;
    max-width: 95%;
  }

  @media (max-width: 768px) {
    width: 100%;
    max-width: 100%;
  }
`;

const CalendarNavigation = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 30px;

  @media (max-width: 768px) {
    margin-bottom: 20px;
  }
`;

const NavButton = styled.button`
  background: transparent;
  border: none;
  cursor: pointer;
  padding: 12px;
  color: #333;
  font-size: 18px;

  &:hover {
    color: #0066cc;
  }

  @media (max-width: 768px) {
    padding: 8px;
    font-size: 16px;
  }
`;

const CalendarMonthsWrapper = styled.div`
  display: flex;
  justify-content: space-between;
  width: 100%;
  position: relative;
  overflow: visible;

  &::after {
    content: "";
    position: absolute;
    top: 10px;
    bottom: 10px;
    left: 50%;
    width: 1px;
    background-color: #e0e0e0;
  }

  @media (max-width: 991px) {
    flex-direction: column;

    &::after {
      display: none;
    }
  }
`;

const CalendarMonth = styled.div`
  flex: 1;
  padding: 0 15px;

  @media (max-width: 991px) {
    padding: 0 10px;
    margin-bottom: 30px;

    &:last-child {
      margin-bottom: 0;
    }
  }

  @media (max-width: 480px) {
    padding: 0 5px;
  }
`;

const MonthHeader = styled.div`
  text-align: center;
  font-size: 18px;
  font-weight: 600;
  margin-bottom: 20px;
  text-transform: uppercase;

  @media (max-width: 768px) {
    font-size: 16px;
    margin-bottom: 15px;
  }
`;

const WeekdayHeader = styled.div`
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  margin-bottom: 15px;

  @media (max-width: 768px) {
    margin-bottom: 10px;
  }
`;

const WeekdayCell = styled.div`
  text-align: center;
  font-size: 14px;
  font-weight: 600;
  color: #666;
  padding: 10px 0;

  @media (max-width: 768px) {
    font-size: 12px;
    padding: 8px 0;
  }

  @media (max-width: 480px) {
    font-size: 11px;
    padding: 5px 0;
  }
`;

const CalendarGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  gap: 4px;

  @media (max-width: 480px) {
    gap: 2px;
  }
`;

const CalendarCell = styled.div`
  aspect-ratio: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: ${(props) =>
    props.isEmpty ? "default" : props.disabled ? "not-allowed" : "pointer"};
  border-radius: 50%;
  font-size: 14px;
  position: relative;
  transition: all 0.2s ease;
  color: ${(props) => (props.disabled ? "#ccc" : "inherit")};
  text-decoration: ${(props) => (props.disabled ? "line-through" : "none")};
  padding: 8px;

  ${(props) =>
    props.isStart &&
    `
    background-color: #d4af37;
    color: white;
    font-weight: bold;
    z-index: 1;
    border-top-right-radius: 0;
    border-bottom-right-radius: 0;
  `}

  ${(props) =>
    props.isEnd &&
    `
    background-color: #d4af37;
    color: white;
    font-weight: bold;
    z-index: 1;
    border-top-left-radius: 0;
    border-bottom-left-radius: 0;
  `}
  
  ${(props) =>
    props.isInRange &&
    !props.isStart &&
    !props.isEnd &&
    `
    background-color: #f0f8ff;
    border-radius: 0;
  `}
  
  ${(props) =>
    props.isHoverRange &&
    !props.isStart &&
    !props.isEnd &&
    `
    background-color: #e6f2ff;
    border-radius: 0;
  `}
  
  &:not(${(props) => props.isEmpty || props.disabled}):hover {
    background-color: #e6f2ff;
    color: #0066cc;
  }

  @media (max-width: 768px) {
    font-size: 13px;
    padding: 6px;
  }

  @media (max-width: 480px) {
    font-size: 12px;
    padding: 4px;
  }
`;

const DateSelectionInfo = styled.div`
  margin-top: 25px;
  padding: 12px;
  background-color: #f9f9f9;
  border-radius: 4px;
  text-align: center;
  font-size: 15px;

  @media (max-width: 768px) {
    margin-top: 20px;
    padding: 10px;
    font-size: 14px;
  }
`;

const RoomSelection = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 2rem 0;
  width: 100%;
  color: #666644;
  font-family: "Montserrat", sans-serif;
`;

const RoomSelectionTitle = styled.h2`
  font-size: 2.2rem;
  font-weight: 600;
  margin-bottom: 0.5rem;
  text-align: center;
  font-family: "Cormorant Garamond", serif;
  letter-spacing: 0.03em;
`;

const RoomSelectionSubtitle = styled.p`
  font-size: 1.4rem;
  color: #555;
  margin-bottom: 3rem;
  text-align: center;
  font-family: "Cormorant Garamond", serif;
  font-weight: 400;
`;

const SelectionValue = styled.div`
  font-size: 2.5rem;
  font-family: "Cormorant Garamond", serif;
  font-weight: 600;
  min-width: 80px;
  text-align: center;
  color: rgb(102, 102, 68);
`;

const AmenityItem = styled.div`
  font-size: 0.9rem;
  display: flex;
  align-items: center;
  color: #000000;
  padding-right: 0.5rem;
`;

const CTAGroup = styled.div`
  margin: 2rem auto;
  max-width: 500px;
  padding: 0 1rem;
`;

const StyledButton = styled(Button)`
  &&& {
    background: #084c7c;
    border-color: #084c7c;
    color: white;
    font-family: "Montserrat", sans-serif;
    font-weight: 500;
    letter-spacing: 0.05em;
    height: 50px;
    border-radius: 25px;
    transition: all 0.3s ease;

    &:hover {
      background: #063c62;
      transform: translateY(-2px);
      box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
    }
  }
`;

const RoomTypeHeader = styled.h3`
  font-size: 1.5rem;
  font-weight: 600;
  color: #333;
  margin: 0 0 1rem;
  font-family: "Cormorant Garamond", serif;
  letter-spacing: 0.02em;
`;

const SectionTitle1 = styled.h2`
  font-size: 1.6rem;
  font-weight: 600;
  margin-bottom: 1.8rem;
  color: #222;
  position: relative;
  font-family: "Cormorant Garamond", serif;
  letter-spacing: 0.02em;

  &:after {
    content: "";
    position: absolute;
    bottom: -10px;
    left: 0;
    width: 50px;
    height: 3px;
    background-color: #b99028;
  }
`;

const SelectionWarning = styled.div`
  margin-top: 1rem;
  padding: 0.75rem 1rem;
  background-color: #fff3cd;
  color: #856404;
  border-left: 4px solid #ffeeba;
  border-radius: 4px;
  font-size: 0.9rem;
  line-height: 1.5;
  text-align: center;
  font-family: "Montserrat", sans-serif;
`;

const RoomTypeRow = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 1rem;
  padding: 0.75rem 1rem;
  background-color: #f9f9f9;
  border-radius: 8px;
  transition: all 0.2s ease;

  &:hover {
    background-color: #f0f0f0;
  }
`;

const RoomCounter = styled.div`
  display: flex;
  align-items: center;
  background-color: #f5f5f5;
  border-radius: 20px;
  padding: 0.25rem;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
`;

const CounterValue = styled.span`
  min-width: 30px;
  text-align: center;
  font-weight: 600;
  font-family: "Montserrat", sans-serif;
`;

const StepperButton = styled.button`
  width: 35px;
  height: 35px;
  border: 1px solid #ddd;
  background: white;
  border-radius: 50%;
  font-size: 1.2rem;
  cursor: pointer;
  transition: all 0.2s;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);

  &:hover {
    border-color: #b99028;
    color: #b99028;
    transform: scale(1.05);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    transform: none;
  }
`;

const RoomDescription = styled.p`
  font-size: 0.95rem;
  line-height: 1.7;
  color: #555;
  margin-bottom: 1.5rem;
  font-family: "Montserrat", sans-serif;
`;

const RoomCardNew = styled.div`
  display: flex;
  background-color: white;
  border: 1px solid #e0e0e0;
  overflow: hidden;
  margin-bottom: 2rem;

  @media (max-width: 1200px) {
    flex-direction: column;
  }
`;

const RoomImageCol = styled.div`
  width: 40%;
  min-width: 300px;

  @media (max-width: 1200px) {
    width: 100%;
    height: 300px;
  }
`;

const RoomImg = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;

  @media (max-width: 1200px) {
    height: 300px;
  }
`;

const RoomDetailsCol = styled.div`
  width: 60%;
  padding: 1.5rem;
  position: relative;
  overflow: auto;

  @media (max-width: 1440px) {
    width: 55%;
    position: relative;
    margin-left: 8%;
  }

  @media (max-width: 768px) {
    width: 95%;
    position: relative;
    margin-top: 6rem;
  }
`;

const AmenitiesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(350px, 1fr));
  gap: 0.75rem;
  margin-bottom: 2rem;

  @media (max-width: 1440px) {
    grid-template-columns: repeat(3, 1fr);
  }

  @media (max-width: 768px) {
    grid-template-columns: repeat(2, 1fr);
  }
`;
export default BookNowModal;
